import { RouterProvider, createBrowserRouter, Outlet } from "react-router-dom";
import * as Sentry from "@sentry/react";

import {
  ActivationOutlet,
  PrivateOutlet,
  PublicOutlet,
} from "./components/outlets";
import { ROUTES } from "./routes";
import { ActivationPage } from "./pages/Activation";
import { AddNumberPage } from "./pages/AddNumber";
import { BuyPage } from "./pages/Buy";
//import { CashOut } from "./pages/CashOut";
import { ConfirmNumberPage } from "./pages/ConfirmNumber";
import { ContactList } from "./pages/ContactList";
import { ContactLinkConfirmation } from "./pages/ContactList/ContactLinkConfirmation";
import { HomePage } from "./pages/Home";
import { InvoicePage } from "./pages/Invoice";
import { InvoiceBusinessPage } from "./pages/InvoiceBusiness";
import { InvoiceDetailsPage } from "./pages/InvoiceDetails";
import { InvoiceListPage } from "./pages/InvoiceList";
import { AnonKYCPage } from "./pages/AnonKYC";
import { LoginPage } from "./pages/Login";
import { NewEmailConfirmationPage } from "./pages/NewEmailConfirmation";
import { ProfilePage } from "./pages/Profile";
import { RecoveryPage } from "./pages/Recovery";
import { RecoveryPassPage } from "./pages/RecoveryPass";
import { SignupPage } from "./pages/Signup";
import { SettingsContactsPage } from "./pages/SettingsContacts";
import { SettingsSecurityPage } from "./pages/SettingsSecurity";
import {
  AccountDeletedPage,
  AuthConfirmPage,
  AuthLinkExpiredPage,
  LinkExpiredPage,
  MaintenancePage,
  NotFoundPage,
  OnmetaKYCFailure,
  OnmetaKYCSuccess,
  PhoneConfirmedPage,
  VerificationFailedPage,
  VerificationSuccessPage,
  VerificationSuspectedPage,
} from "./pages/Static";
//import { PaymentRequestHomePage } from "./pages/PaymentRequestHome";
import { PaymentRequestLinkPage } from "./pages/PaymentRequestLinkPublic";
import { PaymentRequestPage } from "./pages/PaymentRequest";
import { PaymentRequestDocumentPage } from "./pages/PaymentRequestDocument";
import { PublicCalculatorPage } from "./pages/PublicCalculator";
import { VerifyPage } from "./pages/Verify";
import { WalletsPage } from "./pages/Wallets";
import { InvoiceUserType } from "./types/InvoiceTypes";
import { ApplicationErrorBoundary } from "./components/errors";
import { USDPage } from "./pages/USDAccount";
import { DepositPage } from "./pages/Deposit";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const routes = [
  {
    element: (
      <ApplicationErrorBoundary>
        <Outlet />
      </ApplicationErrorBoundary>
    ),
    children: [
      {
        path: ROUTES.HOME,
        element: <HomePage />,
      },
      {
        path: ROUTES.INVOICE_BUSSINES_VIEW,
        element: <InvoiceBusinessPage />,
      },
      {
        path: ROUTES.PAYMENT_REQUEST_PUBLIC,
        element: <PaymentRequestLinkPage />,
      },
      {
        path: ROUTES.PUBLIC_CALCULATOR,
        element: <PublicCalculatorPage />,
      },
      {
        path: ROUTES.KYC,
        element: <AnonKYCPage />,
      },
      {
        path: "/",
        element: <PublicOutlet />,
        children: [
          {
            path: ROUTES.AUTH_LOGIN,
            element: <LoginPage />,
          },
          {
            path: ROUTES.AUTH_ACTIVATION,
            element: <ActivationPage />,
          },
          {
            path: ROUTES.AUTH_RECOVERY,
            element: <RecoveryPage />,
          },
          {
            path: ROUTES.AUTH_NEW_PASS,
            element: <RecoveryPassPage />,
          },
          {
            path: ROUTES.AUTH_VERIFY,
            element: <VerifyPage />,
          },
          {
            path: ROUTES.AUTH_SIGNUP,
            element: <SignupPage />,
          },
          {
            path: ROUTES.AUTH_CONFIRM,
            element: <AuthConfirmPage />,
          },
        ],
      },
      {
        path: "/",
        element: <ActivationOutlet />,
        children: [
          {
            path: ROUTES.AUTH_ADD_NUMBER,
            element: <AddNumberPage />,
          },
          {
            path: ROUTES.AUTH_CONFIRM_NUMBER,
            element: <ConfirmNumberPage />,
          },
        ],
      },
      {
        path: "/",
        element: <PrivateOutlet />,
        children: [
          {
            path: ROUTES.AUTH_PHONE_CONFIRMED,
            element: <PhoneConfirmedPage />,
          },
          {
            path: ROUTES.INVOICE,
            element: <InvoicePage />,
          },
          {
            path: ROUTES.KYC,
            element: <AnonKYCPage />,
          },
          {
            path: ROUTES.PROFILE_GENERAL,
            element: <ProfilePage />,
          },
          {
            path: ROUTES.PROFILE_CONTACTS,
            element: <SettingsContactsPage />,
          },
          {
            path: ROUTES.PROFILE_SECURITY,
            element: <SettingsSecurityPage />,
          },
          {
            path: ROUTES.PROFILE_INVOICE,
            element: <InvoiceDetailsPage />,
          },
          {
            path: ROUTES.WALLETS,
            element: <WalletsPage />,
          },
          {
            path: ROUTES.CONTACTS,
            element: <ContactList />,
          },
          {
            path: ROUTES.ACCOUNT_DELETED,
            element: <AccountDeletedPage />,
          },
          {
            path: ROUTES.PAY,
            element: <InvoiceListPage userIs={InvoiceUserType.PAYER} />,
          },
          {
            path: ROUTES.RECEIVE,
            element: <InvoiceListPage userIs={InvoiceUserType.ISSUER} />,
          },
          {
            path: ROUTES.BUY,
            element: <BuyPage />,
          },
          {
            path: ROUTES.DEPOSIT,
            element: <DepositPage />,
          },
          {
            path: ROUTES.USD_ACCOUNT,
            element: <USDPage />,
          },
          // {
          //   path: ROUTES.CASH_OUT,
          //   element: <CashOut />,
          // },
          {
            path: ROUTES.ONMETA_KYC_FAILURE,
            element: <OnmetaKYCFailure />,
          },
          {
            path: ROUTES.ONMETA_KYC_SUCCESS,
            element: <OnmetaKYCSuccess />,
          },
          // {
          //   path: ROUTES.PAYMENT_REQUEST_HOME,
          //   element: <PaymentRequestHomePage />,
          // },
          {
            path: ROUTES.PAYMENT_REQUESTS,
            element: <PaymentRequestPage />,
          },
          {
            path: ROUTES.PAYMENT_REQUEST,
            element: <PaymentRequestDocumentPage />,
          },
          {
            path: ROUTES.CHANGE_EMAIL,
            element: <NewEmailConfirmationPage />,
          },
        ],
      },
      {
        path: ROUTES.AUTH_LINK_EXPIRED,
        element: <AuthLinkExpiredPage />,
      },
      {
        path: ROUTES.LINK_EXPIRED,
        element: <LinkExpiredPage />,
      },
      {
        path: ROUTES.MAINTENANCE,
        element: <MaintenancePage />,
      },
      {
        path: ROUTES.VERIFICATION_FAIL,
        element: <VerificationFailedPage />,
      },
      {
        path: ROUTES.VERIFICATION_SUSPECTED,
        element: <VerificationSuspectedPage />,
      },
      {
        path: ROUTES.VERIFIED,
        element: <VerificationSuccessPage />,
      },
      {
        path: ROUTES.CONTACT_LINK_CONFIRMATION,
        element: <ContactLinkConfirmation />,
      },
      {
        path: ROUTES.NOT_FOUND,
        element: <NotFoundPage />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];

const router = sentryCreateBrowserRouter(routes);

export const AppRouterProvider = () => <RouterProvider router={router} />;
